/**
 * Refinanciamiento de Préstamos — migración web de ares2050 (escritorio).
 *
 * Cancela un préstamo activo y abre uno nuevo (tipo 'R') por el monto que
 * DECIDE EL OPERADOR: el escritorio ni siquiera calculaba el saldo — la web
 * lo sugiere (el balance del préstamo) pero el financiamiento sigue
 * siendo editable. El préstamo viejo queda status 'C' con fcancel y, si es
 * serializado, el producto se reasigna al préstamo nuevo (todo en el backend
 * /api/refinanciamiento_guardar). Guardar exige autorización aprobada de un
 * AdmG (el backend responde 409 requiereAutorizacion y aquí se solicita y
 * pollea igual que el externo/ares2010).
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SaveIcon from "@mui/icons-material/Save";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";
import { calcularCuotas, useConfigCuotas, mananaISO, unMesISO } from "lib/calculocuotas";
import CampoCuota from "components/cuotas/CampoCuota";

const FORMAS_PAGO = ["Meses", "Quincenas", "Semanas", "Dias"];

const filtrarPrestamos = createFilterOptions({
  stringify: (o) =>
    typeof o === "string"
      ? o
      : `${o.noprest} ${o.nombres || ""} ${o.cedula || ""} ${o.chasis || ""}`,
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

export default function RefinanciamientoInterno({
  embedded = false,
  onExit,
  onSaved,
  noprestInicial,
} = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const { redondeo, elegirPrimeraCuota } = useConfigCuotas();

  const [prestamos, setPrestamos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [prestamo, setPrestamo] = useState(null); // opción del autocomplete
  const [datos, setDatos] = useState(null); // detalle de refinanciamiento_datos
  const [buscando, setBuscando] = useState(false);

  const [valor, setValor] = useState("");
  const [formadepago, setFormadepago] = useState("");
  const [interes, setInteres] = useState("");
  const [plazo, setPlazo] = useState("");
  const [fechaprimeracuota, setFechaprimeracuota] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [guardando, setGuardando] = useState(false);

  // ---- Autorización (mismo contrato que el externo/ventaproductos) ----
  const [autOpen, setAutOpen] = useState(false);
  const [autMotivo, setAutMotivo] = useState("");
  const [autIdtext, setAutIdtext] = useState("");
  const [autStatus, setAutStatus] = useState(""); // "" | Progreso | Aprobado | Cancelado
  const [autComentario, setAutComentario] = useState("");
  const pollRef = useRef(null);

  const detenerPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };
  useEffect(() => detenerPolling, []);

  const cargarPrestamos = async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/refinanciamiento_prestamos", {});
      if (ok) setPrestamos(data.prestamos || []);
      else
        showAlert({ message: data.Error || "No se pudieron cargar los préstamos", color: "error" });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarPrestamos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const limpiar = () => {
    setPrestamo(null);
    setDatos(null);
    setValor("");
    setFormadepago("");
    setInteres("");
    setPlazo("");
    setFechaprimeracuota("");
    setConfirmOpen(false);
  };

  // Preselección: al abrir desde el expediente ya viene el préstamo elegido.
  const [preselListo, setPreselListo] = useState(false);
  useEffect(() => {
    if (!noprestInicial || preselListo || !prestamos.length) return;
    const opcion = prestamos.find((x) => String(x.noprest) === String(noprestInicial));
    if (opcion) elegirPrestamo(opcion);
    setPreselListo(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prestamos]);

  const elegirPrestamo = async (p) => {
    setPrestamo(p);
    setDatos(null);
    setValor("");
    setFormadepago("");
    setInteres("");
    setPlazo("");
    setFechaprimeracuota("");
    if (!p) return;
    setBuscando(true);
    try {
      const { ok, data } = await post("/api/refinanciamiento_datos", { noprest: p.noprest });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo recuperar el préstamo", color: "error" });
        setPrestamo(null);
        return;
      }
      setDatos(data.prestamo);
      setValor(String(data.prestamo.saldosugerido ?? ""));
    } finally {
      setBuscando(false);
    }
  };

  // Vista previa local del nuevo financiamiento.
  const preview = useMemo(
    () => calcularCuotas(toNum(valor), toNum(plazo), formadepago, toNum(interes), redondeo),
    [valor, plazo, formadepago, interes, redondeo]
  );

  const validar = () => {
    if (!datos) {
      showAlert({ message: "Debe seleccionar el préstamo a refinanciar", color: "warning" });
      return false;
    }
    if (toNum(valor) <= 0) {
      showAlert({ message: "El financiamiento no puede estar en 0", color: "warning" });
      return false;
    }
    if (!formadepago) {
      showAlert({ message: "Debe indicar la forma de pago", color: "warning" });
      return false;
    }
    if (toNum(interes) <= 0) {
      showAlert({ message: "El interés no puede estar en 0", color: "warning" });
      return false;
    }
    if (toNum(plazo) <= 0) {
      showAlert({ message: "El plazo no puede estar en 0", color: "warning" });
      return false;
    }
    return true;
  };

  const guardar = async (idautorizacion = "") => {
    setGuardando(true);
    try {
      const { ok, status, data } = await post("/api/refinanciamiento_guardar", {
        noprest: datos.noprest,
        valor: toNum(valor),
        formadepago,
        fechaprimeracuota: elegirPrimeraCuota ? fechaprimeracuota : "",
        interes: toNum(interes),
        plazo: toNum(plazo),
        idautorizacion,
      });
      if (ok) {
        showAlert({
          message: `${data.ok || "Refinanciamiento registrado"} — Préstamo nuevo #${
            data.noprest
          } (cuota RD$ ${fmtMoney(data.valorcuota)}). El préstamo #${
            data.noprestviejo
          } quedó cancelado`,
          color: "success",
        });
        setAutOpen(false);
        limpiar();
        cargarPrestamos();
        if (onSaved) onSaved();
        return;
      }
      if (status === 409 && data.requiereAutorizacion) {
        // El refinanciamiento siempre requiere autorización de un AdmG.
        setConfirmOpen(false);
        setAutMotivo(data.Error || "El refinanciamiento requiere autorización de un AdmG");
        setAutIdtext("");
        setAutStatus("");
        setAutComentario("");
        setAutOpen(true);
        return;
      }
      showAlert({
        message: data.Error || "No se pudo registrar el refinanciamiento",
        color: "error",
      });
      setConfirmOpen(false);
    } finally {
      setGuardando(false);
    }
  };

  const solicitarAutorizacion = async () => {
    const { ok, data } = await post("/api/ventaproducto_solicitarautorizacion", {
      descripcion: `Refinanciamiento del préstamo #${datos?.noprest} cliente ${
        `${datos?.nombres || ""} ${datos?.apellidos || ""}`.trim() || datos?.cedula || ""
      }`,
      tipo: "Aut.Prestamo",
      datos: {
        cedula: datos?.cedula || "",
        nombre: datos?.nombres || "",
        apellido: datos?.apellidos || "",
        prestamoanterior: String(datos?.noprest || ""),
        saldosugerido: fmtMoney(datos?.saldosugerido),
        financia: fmtMoney(toNum(valor)),
        plazo,
        formadepago,
        interes,
        valorcuota: preview ? preview.valorcuota.toFixed(2) : "",
        deudatotal: preview ? preview.deudatotal.toFixed(2) : "",
      },
    });
    if (!ok) {
      showAlert({ message: data.Error || "No se pudo solicitar la autorización", color: "error" });
      return;
    }
    setAutIdtext(data.idtext);
    setAutStatus("Progreso");
    detenerPolling();
    pollRef.current = setInterval(async () => {
      const r = await post("/api/ventaproducto_estadoautorizacion", { idtext: data.idtext });
      if (!r.ok) return;
      const st = r.data.status;
      if (st === "Aprobado") {
        detenerPolling();
        setAutStatus("Aprobado");
        // Aprobada: se reintenta el guardar con el id de la autorización.
        guardar(data.idtext);
      } else if (st === "Cancelado") {
        detenerPolling();
        setAutStatus("Cancelado");
        setAutComentario(r.data.comentario || "");
      }
    }, 5000);
  };

  const cancelarAutorizacion = async () => {
    detenerPolling();
    if (autIdtext && autStatus === "Progreso") {
      await post("/api/ventaproducto_cancelarautorizacion", { idtext: autIdtext });
    }
    setAutOpen(false);
    setAutIdtext("");
    setAutStatus("");
    setAutComentario("");
  };

  const chipInfo = {
    bgcolor: FP.color.surfaceAlt,
    color: FP.color.textSecondary,
    fontWeight: 700,
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Refinanciamiento de Préstamos"
        subtitle="Cancela el préstamo actual y abre uno nuevo por el saldo acordado (ares2050)"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nuevo
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Préstamo a refinanciar */}
        <Box sx={sectionBox}>
          <SectionTitle icon={RequestQuoteOutlinedIcon} color={FP.color.accentBlue}>
            Préstamo a Refinanciar
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarPrestamos}
                options={prestamos}
                loading={cargando || buscando}
                value={prestamo}
                getOptionLabel={(o) =>
                  typeof o === "string" ? o : `${o.noprest} — ${o.nombres || ""}`
                }
                isOptionEqualToValue={(o, v) => (o.noprest || o) === (v.noprest || v)}
                onChange={(_, v) => elegirPrestamo(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.noprest}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.noprest} — {o.nombres}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.cedula, o.chasis, o.sucursal].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Préstamo"
                    helperText="Busca por número, nombre, cédula o chasis"
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
            {datos && (
              <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={`${datos.nombres} ${datos.apellidos}`.trim()} sx={chipInfo} />
                  <Chip label={`Cédula ${datos.cedula || "—"}`} sx={chipInfo} />
                  {datos.serializado && (
                    <Chip
                      label={[datos.marca, datos.modelo, datos.colore].filter(Boolean).join(" · ")}
                      sx={chipInfo}
                    />
                  )}
                  {datos.serializado && <Chip label={`Chasis ${datos.chasis}`} sx={chipInfo} />}
                  {datos.garantia && <Chip label={`Garantía: ${datos.garantia}`} sx={chipInfo} />}
                  <Chip
                    label={`Saldo sugerido: RD$ ${fmtMoney(datos.saldosugerido)}`}
                    sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
                  />
                </Stack>
              </Grid>
            )}
            {datos?.comentario && (
              <Grid item xs={12}>
                <Typography sx={{ fontSize: "0.8rem", color: FP.color.textMuted }}>
                  Comentario de la solicitud: {datos.comentario}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Nuevo financiamiento */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <SectionTitle icon={PaymentsOutlinedIcon} color={FP.color.success}>
            Nuevo Financiamiento
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                type="number"
                label="Financiamiento"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                disabled={!datos}
                helperText="Precargado con el saldo sugerido — editable"
                sx={fpInputSx}
                InputProps={{
                  startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                select
                fullWidth
                label="Forma de pago"
                value={formadepago}
                onChange={(e) => setFormadepago(e.target.value)}
                disabled={!datos}
                sx={fpInputSx}
              >
                {FORMAS_PAGO.map((f) => (
                  <MenuItem key={f} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {elegirPrimeraCuota && (
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha primera cuota"
                  value={fechaprimeracuota}
                  onChange={(e) => setFechaprimeracuota(e.target.value)}
                  disabled={!datos}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: mananaISO(), max: unMesISO() }}
                  sx={fpInputSx}
                  helperText="Vacía = automática; hasta un mes"
                />
              </Grid>
            )}
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                type="number"
                label="Interés (mensual)"
                value={interes}
                onChange={(e) => setInteres(e.target.value)}
                disabled={!datos}
                sx={fpInputSx}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                type="number"
                label="Plazo (cuotas)"
                value={plazo}
                onChange={(e) => setPlazo(e.target.value)}
                disabled={!datos}
                sx={fpInputSx}
              />
            </Grid>
            {redondeo && (
              <Grid item xs={6} sm={3}>
                <CampoCuota
                  editable={!!datos}
                  label="Valor cuota"
                  cuota={preview ? preview.valorcuota : null}
                  financia={toNum(valor)}
                  plazo={toNum(plazo)}
                  formadepago={formadepago}
                  onInteres={setInteres}
                  disabled={!datos}
                  sx={fpInputSx}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={8} sx={{ display: "flex", alignItems: "center" }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={`Valor cuota: RD$ ${preview ? fmtMoney(preview.valorcuota) : "0.00"}`}
                  sx={chipInfo}
                />
                <Chip
                  label={`Deuda total: RD$ ${preview ? fmtMoney(preview.deudatotal) : "0.00"}`}
                  sx={chipInfo}
                />
              </Stack>
            </Grid>
            <Grid
              item
              xs={12}
              sm={4}
              sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
            >
              <Button
                startIcon={
                  guardando ? <CircularProgress size={16} color="inherit" /> : <AutorenewIcon />
                }
                onClick={() => {
                  if (validar()) setConfirmOpen(true);
                }}
                disabled={guardando || !datos}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Refinanciar
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Confirmación */}
      <Dialog
        open={confirmOpen}
        onClose={() => !guardando && setConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <WarningAmberOutlinedIcon sx={{ color: FP.color.warning }} />
          Confirmar refinanciamiento
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", color: FP.color.textSecondary }}>
            El préstamo <b>#{datos?.noprest}</b> quedará <b>CANCELADO</b> y se abrirá uno nuevo por{" "}
            <b>RD$ {fmtMoney(valor)}</b>
            {preview
              ? ` (${plazo} ${String(formadepago).toLowerCase()} de RD$ ${fmtMoney(
                  preview.valorcuota
                )})`
              : ""}
            . ¿Desea continuar?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={guardando} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              guardando ? <CircularProgress size={16} color="inherit" /> : <AutorenewIcon />
            }
            onClick={() => guardar()}
            disabled={guardando}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Sí, refinanciar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de autorización (mismo flujo que el externo) */}
      <Dialog
        open={autOpen}
        onClose={() => {}}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <GppMaybeOutlinedIcon sx={{ color: FP.color.warning }} />
          Se requiere autorización
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", color: FP.color.textSecondary, mb: 2 }}>
            {autMotivo}
          </Typography>

          {autStatus === "" && (
            <Typography sx={{ fontSize: "0.84rem", color: FP.color.textMuted }}>
              Haz clic en &quot;Solicitar autorización&quot; para enviar la petición al
              administrador.
            </Typography>
          )}
          {autStatus === "Progreso" && (
            <Stack direction="row" spacing={1.4} alignItems="center">
              <CircularProgress size={18} />
              <Chip
                label="Esperando autorización..."
                sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
              />
            </Stack>
          )}
          {autStatus === "Aprobado" && (
            <Stack direction="row" spacing={1.4} alignItems="center">
              <CheckCircleOutlineIcon sx={{ color: FP.color.success }} />
              <Typography sx={{ fontSize: "0.9rem", color: FP.color.success, fontWeight: 700 }}>
                Autorización aprobada. Guardando el refinanciamiento…
              </Typography>
            </Stack>
          )}
          {autStatus === "Cancelado" && (
            <Typography sx={{ fontSize: "0.9rem", color: FP.color.danger, fontWeight: 700 }}>
              Autorización declinada{autComentario ? ` — mensaje: ${autComentario}` : ""}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={cancelarAutorizacion} sx={btnGhost}>
            Cerrar
          </Button>
          {autStatus === "" && (
            <Button
              disableElevation
              onClick={solicitarAutorizacion}
              sx={btnSolid(FP.color.warning, "#B45309")}
            >
              Solicitar autorización
            </Button>
          )}
          {autStatus === "Aprobado" && (
            <Button
              disableElevation
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={guardando}
              onClick={() => guardar(autIdtext)}
              sx={btnSolid(FP.color.success, FP.color.successDark)}
            >
              Guardar con autorización
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
